// static/table_engine_src/core/ops_columns.js

function isLockedCol(col) {
  return !!col?.locked;
}

/**
 * Installs on ctx:
 *   - ctx.setColumnHidden(colKey, hidden)
 *   - ctx.setColumnsHidden(colKeys, hidden)
 *   - ctx.unhideAllColumns()
 */
export function installColumnOps(ctx) {
  if (!ctx || typeof ctx !== "object") return;

  const { table, thead, hiddenCols, colByKey, escCss } = ctx;
  if (!table || !thead || !hiddenCols || !colByKey) return;

  function pushHistoryAction(action) {
    if (!action || typeof action !== "object") return;
    if (typeof ctx.pushHistoryAction !== "function") return;
    ctx.pushHistoryAction({
      ...action,
      ts: Number.isFinite(action.ts) ? action.ts : Date.now(),
    });
  }

  function finalizeColumnVisibility() {
    ctx.applySelectionVisuals?.();
    ctx.updateTotals?.();
    ctx.scheduleNotifyRendered?.();
    ctx.persistLayoutProfile?.();
  }

  function applyColumnHiddenState(colKey, hidden, { notifyLocked = true } = {}) {
    if (!colKey) return { changed: false };

    const ROW_HANDLE_KEY = ctx.ROW_HANDLE_KEY;
    if (ctx.rowMenuEnabled && colKey === ROW_HANDLE_KEY) return { changed: false };

    const col = colByKey.get(colKey);
    if (hidden && isLockedCol(col)) {
      if (notifyLocked) {
        ctx.notify?.err?.(`Cannot hide protected column: ${col?.header || col?.key || colKey}`);
      }
      return { changed: false };
    }

    const before = hiddenCols.has(colKey);
    const next = !!hidden;
    if (before === next) return { changed: false, before, after: next };

    if (next) hiddenCols.add(colKey);
    else hiddenCols.delete(colKey);

    const cg = table.querySelector("colgroup");
    if (cg) {
      const colEl = cg.querySelector(`col[data-col-key="${escCss(colKey)}"]`);
      if (colEl) colEl.style.display = next ? "none" : "";
    }

    const th = thead.querySelector(`th[data-col-key="${escCss(colKey)}"]`);
    if (th) th.style.display = next ? "none" : "";

    const idx = ctx.colIndexByKey?.(colKey) ?? -1;
    if (idx >= 0) {
      (ctx.getRowEls?.() || []).forEach((tr) => {
        const td = tr.children[idx];
        if (td) td.style.display = next ? "none" : "";
      });
    }

    if (next && ctx._sel?.selectedCols?.has?.(colKey)) {
      ctx._sel.selectedCols.delete(colKey);
    }

    return { changed: true, before, after: next };
  }

  function applyColumnEntries(entries = [], dir = "redo") {
    let changed = false;
    for (const entry of entries) {
      if (!entry || !entry.colKey) continue;
      const targetHidden = dir === "undo" ? !!entry.before : !!entry.after;
      const res = applyColumnHiddenState(entry.colKey, targetHidden, { notifyLocked: false });
      if (res.changed) changed = true;
    }
    return changed;
  }

  function normalizeColumnKeys(colKeys) {
    return [...new Set((colKeys || []).filter((k) => !!k))];
  }

  function setColumnsHidden(colKeys, hidden, { recordHistory = true } = {}) {
    const keys = normalizeColumnKeys(colKeys);
    if (!keys.length) return 0;

    const entries = [];
    keys.forEach((colKey) => {
      const res = applyColumnHiddenState(colKey, hidden, { notifyLocked: true });
      if (!res.changed) return;
      entries.push({
        colKey,
        before: !!res.before,
        after: !!res.after,
      });
    });

    if (!entries.length) return 0;

    finalizeColumnVisibility();

    if (recordHistory) {
      pushHistoryAction({
        type: "columns-visibility",
        label: hidden ? "hide-columns" : "unhide-columns",
        undo: () => {
          applyColumnEntries(entries, "undo");
          finalizeColumnVisibility();
        },
        redo: () => {
          applyColumnEntries(entries, "redo");
          finalizeColumnVisibility();
        },
      });
    }

    return entries.length;
  }

  function setColumnHidden(colKey, hidden, { recordHistory = true } = {}) {
    return setColumnsHidden([colKey], hidden, { recordHistory }) > 0;
  }

  function unhideAllColumns({ recordHistory = true } = {}) {
    const ROW_HANDLE_KEY = ctx.ROW_HANDLE_KEY;
    const keys = [...hiddenCols].filter((k) => !(ctx.rowMenuEnabled && k === ROW_HANDLE_KEY));
    if (!keys.length) return 0;

    const entries = keys.map((k) => ({ colKey: k, before: true, after: false }));

    ctx.setSuppressRenderNotify?.(true);
    const changed = applyColumnEntries(entries, "redo");
    ctx.renderColgroup?.();
    ctx.renderHeader?.();
    ctx.setSuppressRenderNotify?.(false);

    if (!changed) return 0;

    finalizeColumnVisibility();

    if (recordHistory) {
      pushHistoryAction({
        type: "columns-unhide-all",
        label: "unhide-all-columns",
        undo: () => {
          ctx.setSuppressRenderNotify?.(true);
          applyColumnEntries(entries, "undo");
          ctx.renderColgroup?.();
          ctx.renderHeader?.();
          ctx.setSuppressRenderNotify?.(false);
          finalizeColumnVisibility();
        },
        redo: () => {
          ctx.setSuppressRenderNotify?.(true);
          applyColumnEntries(entries, "redo");
          ctx.renderColgroup?.();
          ctx.renderHeader?.();
          ctx.setSuppressRenderNotify?.(false);
          finalizeColumnVisibility();
        },
      });
    }

    return entries.length;
  }

  ctx.setColumnHidden = setColumnHidden;
  ctx.setColumnsHidden = setColumnsHidden;
  ctx.unhideAllColumns = unhideAllColumns;
}
